import { t } from "i18next";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image } from "../types/models/Image";

interface FileInputProps {
  images: Image[];
  setImages: (images: Image[]) => void;
  onAddImage: (file: File) => void;
  onRemoveImage: (id: number) => void;
  accept?: string;
  label?: string;
  maxImages?: number;
}

const SmartImageUploader: React.FC<FileInputProps> = ({
  images,
  setImages,
  onAddImage,
  onRemoveImage,
  accept = "image/jpeg, image/png, image/jpg",
  label = t("addPicturesModal.upload"),
  maxImages = 4,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFile = files[0];
      handleAddImage(newFile);
    }
  };

  const handleAddImage = (file: File) => {
    if (images.length >= maxImages) {
      alert(t("addPicturesModal.maxImagesReached", { maxImages }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const newImages = [
        { file: file, url: reader.result as string },
        ...images,
      ];
      setImages(newImages);
      onAddImage(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (index: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newImages = [...images];
    const deletedImage = newImages.splice(index, 1);
    setImages(newImages);
    if (deletedImage[0].id !== undefined) {
      onRemoveImage(deletedImage[0].id);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleAddImage(files[0]);
    }
  };

  const handleClick = () => {
    if (images.length < maxImages) {
      fileInputRef.current?.click();
    }
  };

  const showInputInMainArea = images.length <= maxImages - 1;
  const mainAreaImage =
    !showInputInMainArea && images.length > 0 ? images[0] : null;
  const sideImages = mainAreaImage ? images.slice(1, 4) : images;

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-row items-start space-x-4 overflow-y-scroll">
        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center w-60 h-60 bg-delicate-50 rounded-lg cursor-pointer border-2 relative ${
            isDragging
              ? "border-rust-300 border-dashed"
              : "border-slate-300 border-dashed"
          } transition-colors duration-200 ease-in-out flex-shrink-0`}
        >
          {mainAreaImage ? (
            <div className="relative w-full h-full">
              <img
                src={mainAreaImage.url}
                alt="Dernière image"
                className="w-full h-full object-cover rounded-lg"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage(0);
                }}
                className="absolute top-2 right-2 bg-white bg-opacity-70 rounded-full p-1 hover:bg-opacity-100 transition-opacity"
                title="Supprimer l'image"
              >
                <svg
                  className="w-6 h-6 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <div className="h-16 w-16 mb-3 rounded-full border-2 border-rust-300 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-rust-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-rust-300 text-xl font-medium text-center">
                {label}
              </p>
              <p className="text-sm text-slate-500 mt-2">
                {images.length}/{maxImages} images
              </p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={accept}
            onChange={handleFileChange}
            disabled={images.length >= maxImages}
          />
        </div>
        <div className="flex flex-row flex-wrap gap-2">
          {sideImages.map((image, index) => {
            return (
              <div key={index} className="relative w-60 h-60">
                <img
                  src={image.url}
                  alt={`Image ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  onClick={(e) =>
                    removeImage(mainAreaImage ? index + 1 : index, e)
                  }
                  className="absolute top-1 right-1 bg-white bg-opacity-70 rounded-full p-0.5 hover:bg-opacity-100 transition-opacity"
                >
                  <svg
                    className="w-4 h-4 text-red-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-sm my-0 text-slate-500">
        {images.length === maxImages
          ? t("addPicturesModal.maxImagesReached", { maxImages })
          : t("addPicturesModal.imageCounter", {
              imageCount: images.length,
              maxImages,
            })}
        <br />
        {t("addPicturesModal.limiteInstructions", { maxImages })}
        <br />
        {t("addPicturesModal.qualityWarning")}
      </p>
    </div>
  );
};

export default SmartImageUploader;
